import { useContext, useEffect, useRef } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { ReactivationPage } from 'components/onboarding/ReactivationPage';
import { ActivatingTenantPage } from 'components/onboarding/ActivatingTenantPage';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { PoweredByTbOneFooter } from 'components/PoweredByTbOneFooter';
import { ErrorAccountPage } from 'components/onboarding/ErrorAccountPage';
import { jwtDecode } from 'jwt-decode';
import { loadUsetifulScript, setUsetifulTags } from 'usetiful-sdk';
import { Sidebar } from 'components/Sidebar/Sidebar';
import { TopHeader } from 'components/TopHeader/TopHeader';
import { useSidebar } from 'hooks/useSidebar';
import { useShouldSkipMatomoTracking } from 'hooks/useShouldSkipMatomoTracking';
import { useTokenPermissions } from 'hooks/useTokenPermissions';
import {
  getMatomoPageTitle,
  getMatomoPermissionDimensionValueFromTenantPermissions,
  installMatomoHeatmapSessionRecordingTrigger,
  MATOMO_CUSTOM_DIMENSION_ACCOUNT_NUMBER,
  MATOMO_CUSTOM_DIMENSION_PERMISSION,
  setMatomoCustomDimension,
  trackMatomoPageView,
} from 'services/matomo.service';
import { runtimeConfig } from 'config/runtimeConfig';

import { AppRoutes as AppRouter } from './approuter';
import { useImageCookie } from 'hooks/useImageCookie';
import { useSentryUser } from 'hooks/useSentryUser';

type UsetifulUserWithProgress = NonNullable<NonNullable<typeof window.USETIFUL>['user']> & {
  clearProgress?: () => void;
};

function AppContent() {
  const tenantConfig = useContext(TenantConfigContext);
  const { sidebarCollapsed, toggleSidebar } = useSidebar();

  const usetifulTokenKey = runtimeConfig.USETIFUL_TOKEN_KEY;
  const matomoContainerId = runtimeConfig.MATOMO_CONTAINER_ID;
  const env = runtimeConfig.ENV_NAME;
  const [tokenPermissions, isPermissionsLoading] = useTokenPermissions();
  const shouldSkipMatomoTracking = useShouldSkipMatomoTracking();

  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
  const previousUsetifulUserId = useRef<string | null>(null);

  useImageCookie();
  useSentryUser();

  loadUsetifulScript(usetifulTokenKey, { identifyUser: true });

  useEffect(() => {
    if (tenantConfig.onboardingStatus === 'UNKNOWN') {
      // Admin user navigated to the app with an account number that is not yet known to the system.
      // Clear the account number from the query param to avoid confusion.
      tenantConfig.setActiveAccountNumber('');
    }

    const updateUsetifulUser = async () => {
      if (!isAuthenticated || !tenantConfig.accountNumber) return;

      const accessToken = await getAccessTokenSilently();
      const decodedToken = jwtDecode(accessToken);
      const userId = `${tenantConfig.accountNumber}-${decodedToken.sub}`;
      const currentAdminView = !!tenantConfig.isAdminView;

      if (previousUsetifulUserId.current !== null && previousUsetifulUserId.current !== userId) {
        const usetifulUser: UsetifulUserWithProgress | undefined = window.USETIFUL?.user;
        usetifulUser?.clearProgress?.();
      }

      previousUsetifulUserId.current = userId;

      setUsetifulTags({
        userId,
        tenantName: tenantConfig.name,
        accountNumber: tenantConfig.accountNumber,
        environment: env,
        permissions: tenantConfig.permissions?.join(',') || '',
        isAdminView: currentAdminView ? 'true' : 'false',
      });
    };

    updateUsetifulUser();
  }, [
    isAuthenticated,
    tenantConfig.accountNumber,
    tenantConfig.name,
    tenantConfig.permissions,
    tenantConfig.isAdminView,
    tenantConfig.onboardingStatus,
  ]);

  useEffect(() => {
    if (!matomoContainerId || !isAuthenticated) {
      return;
    }

    const accountNumber = tenantConfig.accountNumber;
    const isTestTenant = tenantConfig.testTenantFlag;
    const isSmokeAccount = accountNumber === '360smoke';

    // eslint-disable-next-line no-underscore-dangle
    const matomoAsync = (window._paq = window._paq || []);
    const matomoResetKey = 'matomo_cleanup_v3';
    const hasReset = window.localStorage.getItem(matomoResetKey);

    if (!hasReset) {
      matomoAsync.push(['deleteCookies']);
      window.localStorage.setItem(matomoResetKey, 'true');
    }

    if (isPermissionsLoading) {
      return;
    }

    if (!accountNumber) {
      return;
    }

    if (shouldSkipMatomoTracking) {
      return;
    }

    if (user?.sub) {
      matomoAsync.push(['setUserId', user.sub]);
    } else {
      matomoAsync.push(['resetUserId']);
    }

    // Visit-scope Custom Dimensions (scope is configured in Matomo).
    setMatomoCustomDimension(MATOMO_CUSTOM_DIMENSION_ACCOUNT_NUMBER, accountNumber);

    const permissionDimensionValue = getMatomoPermissionDimensionValueFromTenantPermissions(tenantConfig.permissions);
    if (permissionDimensionValue) {
      setMatomoCustomDimension(MATOMO_CUSTOM_DIMENSION_PERMISSION, permissionDimensionValue);
    }

    installMatomoHeatmapSessionRecordingTrigger(!(isSmokeAccount && isTestTenant));

    const d = document;
    const containerSrc = `https://matomo.tradebyte.org/js/container_${matomoContainerId}.js`;
    const existingScript = d.querySelector(`script[src="${containerSrc}"]`);

    if (!existingScript) {
      const g = d.createElement('script');
      const s = d.getElementsByTagName('script')[0];
      g.async = true;
      g.src = containerSrc;
      g.onload = () => {
        trackMatomoPageView(window.location.href, getMatomoPageTitle(window.location.pathname));
      };
      s?.parentNode?.insertBefore(g, s);
    }
  }, [
    isAuthenticated,
    matomoContainerId,
    tenantConfig.accountNumber,
    tenantConfig.isAdminView,
    tenantConfig.testTenantFlag,
    tokenPermissions,
    user?.sub,
    isPermissionsLoading,
    env,
    tenantConfig.permissions,
    shouldSkipMatomoTracking,
  ]);

  const getComponentForActivationStatus = (): React.JSX.Element => {
    switch (tenantConfig?.onboardingStatus) {
      case 'INACTIVE':
        return <ReactivationPage />;
      case 'NEW':
        return <ActivatingTenantPage pageType="NEW" />;
      case 'ACTIVATING':
        return <ActivatingTenantPage pageType="ACTIVATING" />;
      case 'IGNORED_TB_MARKET_ACCOUNT':
        return <ErrorAccountPage errorType="TBMARKET" />;
      case 'IGNORED_NO_ACCOUNT_CHANNEL':
        return <ErrorAccountPage errorType="NO_CHANNEL" />;
      case 'IGNORED_OTHER':
        return <ErrorAccountPage errorType="IGNORED" />;
      default:
        return (
          <>
            <AppRouter />
            <PoweredByTbOneFooter />
          </>
        );
    }
  };

  return (
    <div className="flex relative min-h-screen">
      <Sidebar sidebarCollapsed={sidebarCollapsed} />
      <div className="flex-1 flex flex-col min-h-screen md:ml-0 ml-20">
        <TopHeader sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
        <main className="flex-1 bg-snow-50 flex flex-col">{getComponentForActivationStatus()}</main>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="min-h-screen">
      <AppContent />
    </div>
  );
}

export const AuthenticatedApp = withAuthenticationRequired(App, {
  returnTo: () => window.location.pathname + window.location.search,
});
